import React, { useState, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { TQuestionnaire, TQuestionnaireDetail } from '../../../../types/questionnaires';
import { VitalItemSC } from '../vital-list/vital-list.styled';
import { QuestionnaireWidgetSC } from './questionnaires.styled';
import TStore from '../../../../types/store';
import { actions } from '../../../../store/ducks/questionnaires';
import MenuItem from '../menu-item';
import QuestionnaireDetail from './questionnaire-detail';
import DrawerModal from '../../../../components/drawer-modal';
import VitalItemHeader from '../vital-list/vital-item-header';

type TProps = {
  userId: string;
  questionnaires: TQuestionnaire[];
  getQuestionnairesRequest(providerId: string, profileId: string): void;
  setQuestionnaireDetail(questionnaire: TQuestionnaireDetail): void;
};

const reverseCompare = (a: any, b: any) => {
  if (a.createdAt < b.createdAt) {
    return 1;
  }
  if (a.createdAt > b.createdAt) {
    return -1;
  }
};

const groupByQuestionnaireName = (questionnaires: any[]) => {
  if (questionnaires.length) {
    const groups = questionnaires.reverse().reduce((groups: any, questionnaire) => {
      groups[questionnaire.questionnaire.name] = groups[questionnaire.questionnaire.name] || [];
      groups[questionnaire.questionnaire.name].push(questionnaire);
      return groups;
    }, {});

    const result = [];

    for (const groupName of Object.keys(groups)) {
      const ordered = groups[groupName].sort(reverseCompare);
      result.push({
        name: groupName,
        items: ordered,
      });
    }

    return result;
  }
  return [];
};

const Questionnaires: React.FC<TProps> = ({
  userId,
  questionnaires,
  getQuestionnairesRequest,
  setQuestionnaireDetail,
}) => {
  const [show, setShow] = useState(false);
  const providerId = useSelector((state: TStore) => state.users.business[0].id);

  const intl = useIntl();
  useEffect(() => {
    getQuestionnairesRequest(providerId, userId);
  }, [providerId, getQuestionnairesRequest, userId]);

  const onQuestionnaireClick = (questionnaire: TQuestionnaire) => {
    setQuestionnaireDetail({
      ...questionnaire,
      data: JSON.parse(questionnaire.data),
    });
    setShow(true);
  };

  const onClose = () => {
    setShow(false);
  };

  const grouped = useMemo(() => groupByQuestionnaireName(questionnaires), [questionnaires]);

  if (grouped.length === 0) {
    return null;
  }

  return (
    <QuestionnaireWidgetSC>
      <VitalItemSC>
        <VitalItemHeader
          icon={
            <img
              src={require('../../../../assets/svg/icon-questionnaire.svg').default}
              width="24"
              height="24"
              alt=""
            />
          }
          title="life_id.questionnaire"
        />
        <div className="questionnaire-widget__body">
          <>
            {grouped.map(({ name, items }) => (
              <MenuItem
                key={name}
                name={intl.formatMessage({ id: `questionnaire.${name}` })}
                translateName={false}
                onClick={() => onQuestionnaireClick(items[0])}
              />
            ))}
          </>
        </div>
      </VitalItemSC>
      <DrawerModal visible={show} onClose={onClose}>
        <QuestionnaireDetail />
      </DrawerModal>
    </QuestionnaireWidgetSC>
  );
};

const mapStateToProps = (state: TStore) => ({
  questionnaires: state.questionnaires.questionnaires,
});

const mapDispatchToProps = (dispatch: any) => ({
  getQuestionnairesRequest: (providerId: string, id: string) =>
    dispatch(actions.getQuestionnairesRequest(providerId, id)),
  setQuestionnaireDetail: (payload: TQuestionnaireDetail) =>
    dispatch(actions.setQuestionnaireDetail(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Questionnaires);
